<template>
  <div :class="$style.container">
    <!-- 페이지 헤더 -->
    <div :class="$style.pageHeader">
      <div :class="$style.pageHeaderLeft">
        <button
          :class="$style.headerBackButton"
          @click="$router.back()"
          title="뒤로가기"
          aria-label="뒤로가기"
        >
          ←
        </button>
        <h1 :class="$style.pageTitle">RT 추첨</h1>
      </div>
    </div>

    <!-- 히어로 섹션 -->
    <section :class="$style.hero">
      <div :class="$style.heroBadge">
        <span>X · RT 이벤트</span>
      </div>
      <h2 :class="$style.heroTitle">리트윗 추첨기</h2>
      <p :class="$style.heroSub">
        트윗을 리트윗한 유저 중 무작위로 당첨자를 뽑아드려요.
      </p>
    </section>

    <!-- 스텝 인디케이터 -->
    <nav :class="$style.stepper" aria-label="진행 단계">
      <div
        v-for="(label, idx) in steps"
        :key="label"
        :class="[
          $style.stepperItem,
          { [$style.stepperItemActive]: step === idx + 1, [$style.stepperItemDone]: step > idx + 1 },
        ]"
      >
        <span :class="$style.stepperDot">{{ idx + 1 }}</span>
        <span :class="$style.stepperLabel">{{ label }}</span>
      </div>
    </nav>

    <!-- 본문 -->
    <main :class="$style.main">
      <!-- STEP 1: 트윗 입력 -->
      <section v-if="step === 1" :class="$style.card">
        <h2 :class="$style.cardTitle">1. 추첨할 트윗을 알려주세요</h2>
        <p :class="$style.cardDesc">
          X 트윗 URL을 붙여넣거나, 트윗 ID를 직접 입력할 수 있어요.
        </p>

        <div :class="$style.field">
          <label :class="$style.label" for="tweetUrl">트윗 URL 또는 ID</label>
          <input
            id="tweetUrl"
            v-model.trim="tweetInput"
            :class="$style.input"
            type="text"
            placeholder="예: https://x.com/username/status/1234567890123456789"
            autocomplete="off"
            spellcheck="false"
            @input="onTweetInput"
          />
          <div v-if="parsedTweetId" :class="$style.hintOk">
            <span :class="$style.hintIcon">✓</span>
            인식된 트윗 ID:
            <code :class="$style.code">{{ parsedTweetId }}</code>
          </div>
          <div
            v-else-if="tweetInput && !parsedTweetId"
            :class="$style.hintErr"
          >
            올바른 X(트위터) URL 또는 숫자 ID를 입력해주세요.
          </div>
        </div>

        <div :class="$style.actions">
          <button
            type="button"
            :class="[$style.btn, $style.btnGhost]"
            @click="resetAll"
          >
            초기화
          </button>
          <button
            type="button"
            :class="[$style.btn, $style.btnPrimary]"
            :disabled="!parsedTweetId"
            @click="step = 2"
          >
            다음
          </button>
        </div>
      </section>

      <!-- STEP 2: 참여자 수집 (API or 수동) -->
      <section v-if="step === 2" :class="$style.card">
        <h2 :class="$style.cardTitle">2. 참여자 명단을 가져오세요</h2>
        <p :class="$style.cardDesc">
          X 계정을 연동해 자동으로 가져오거나, 직접 명단을 붙여넣을 수 있어요.
        </p>

        <!-- 소스 토글 -->
        <div :class="$style.tabs" role="tablist">
          <button
            type="button"
            role="tab"
            :class="[$style.tab, { [$style.tabActive]: sourceMode === 'api' }]"
            @click="sourceMode = 'api'"
          >
            X 연동으로 가져오기
          </button>
          <button
            type="button"
            role="tab"
            :class="[$style.tab, { [$style.tabActive]: sourceMode === 'manual' }]"
            @click="sourceMode = 'manual'"
          >
            명단 직접 입력
          </button>
        </div>

        <!-- API 모드 -->
        <div v-if="sourceMode === 'api'" :class="$style.sourcePanel">
          <div v-if="!xAccessToken" :class="$style.connectBox">
            <div :class="$style.connectInfo">
              <h3 :class="$style.connectTitle">X 계정 연동이 필요해요</h3>
              <p :class="$style.connectDesc">
                리트윗한 유저 목록은 X 인증 후에만 조회할 수 있어요.
                연동 정보는 이 브라우저에만 임시 저장되며,
                "연동 해제" 또는 "초기화"로 즉시 삭제됩니다.
              </p>
            </div>
            <button
              type="button"
              :class="[$style.btn, $style.btnX]"
              :disabled="xConnecting"
              @click="connectX"
            >
              <span :class="$style.btnXIcon">𝕏</span>
              {{ xConnecting ? "연동 진행 중…" : "X 계정 연동하기" }}
            </button>
          </div>

          <div v-else :class="$style.connectedBox">
            <div :class="$style.connectedRow">
              <div :class="$style.connectedAvatar">
                <img
                  v-if="xUser && xUser.profile_image_url"
                  :src="xUser.profile_image_url"
                  alt=""
                />
                <span v-else>𝕏</span>
              </div>
              <div :class="$style.connectedMeta">
                <div :class="$style.connectedName">
                  {{ xUser ? xUser.name : "X 계정" }}
                </div>
                <div :class="$style.connectedHandle">
                  @{{ xUser ? xUser.username : "" }}
                </div>
              </div>
              <button
                type="button"
                :class="[$style.btn, $style.btnGhostSmall]"
                @click="disconnectX"
              >
                해제
              </button>
            </div>

            <button
              type="button"
              :class="[$style.btn, $style.btnPrimary, $style.fullWidth]"
              :disabled="fetchingRetweets"
              @click="fetchRetweetUsers"
            >
              <span v-if="fetchingRetweets" :class="$style.spinnerSm"></span>
              {{ fetchingRetweets ? "리트윗 유저 불러오는 중…" : "리트윗 유저 불러오기" }}
            </button>

            <p v-if="apiError" :class="$style.errorMsg">
              {{ apiError }}
            </p>
            <p v-if="apiHint" :class="$style.helperMsg">
              {{ apiHint }}
            </p>
          </div>
        </div>

        <!-- 수동 모드 -->
        <div v-else :class="$style.sourcePanel">
          <div :class="$style.field">
            <label :class="$style.label" for="manualList">참여자 명단</label>
            <textarea
              id="manualList"
              v-model="manualText"
              :class="$style.textarea"
              rows="8"
              placeholder="@username1&#10;@username2&#10;username3"
            ></textarea>
            <div :class="$style.hintMuted">
              한 줄에 한 명씩 또는 쉼표/공백으로 구분해주세요. <code>@</code>는 자동으로 제거됩니다.
            </div>
          </div>
          <button
            type="button"
            :class="[$style.btn, $style.btnPrimary, $style.fullWidth]"
            :disabled="!manualParsed.length"
            @click="applyManualList"
          >
            명단 적용 ({{ manualParsed.length }}명)
          </button>
        </div>

        <!-- 현재 참여자 미리보기 -->
        <div v-if="participants.length" :class="$style.participantsBox">
          <div :class="$style.participantsHeader">
            <h3 :class="$style.participantsTitle">
              참여자 {{ participants.length.toLocaleString() }}명
            </h3>
            <button
              type="button"
              :class="[$style.btn, $style.btnGhostSmall]"
              @click="clearParticipants"
            >
              비우기
            </button>
          </div>

          <ul :class="$style.chipList">
            <li
              v-for="p in participantsPreview"
              :key="p.id || p.username"
              :class="$style.chip"
            >
              <span v-if="p.profile_image_url" :class="$style.chipAvatarImg">
                <img :src="p.profile_image_url" alt="" />
              </span>
              <span v-else :class="$style.chipAvatar">@</span>
              <span :class="$style.chipText">@{{ p.username }}</span>
            </li>
            <li
              v-if="participants.length > participantsPreview.length"
              :class="$style.chipMore"
            >
              외 +{{ participants.length - participantsPreview.length }}명
            </li>
          </ul>
        </div>

        <div :class="$style.actions">
          <button
            type="button"
            :class="[$style.btn, $style.btnGhost]"
            @click="step = 1"
          >
            이전
          </button>
          <button
            type="button"
            :class="[$style.btn, $style.btnPrimary]"
            :disabled="!participants.length"
            @click="step = 3"
          >
            다음
          </button>
        </div>
      </section>

      <!-- STEP 3: 추첨 설정 -->
      <section v-if="step === 3" :class="$style.card">
        <h2 :class="$style.cardTitle">3. 추첨 옵션을 설정하세요</h2>
        <p :class="$style.cardDesc">
          참여자 <b>{{ eligibleCount.toLocaleString() }}명</b> 중에서 추첨합니다.
        </p>

        <div :class="$style.optionGrid">
          <div :class="$style.field">
            <label :class="$style.label" for="winnerCount">당첨자 수</label>
            <input
              id="winnerCount"
              v-model.number="winnerCount"
              :class="$style.input"
              type="number"
              min="1"
              :max="Math.max(1, eligibleCount)"
            />
            <div :class="$style.hintMuted">
              최대 {{ eligibleCount.toLocaleString() }}명까지 가능합니다.
            </div>
          </div>

          <div :class="$style.checkboxGroup">
            <label :class="$style.checkRow">
              <input
                type="checkbox"
                v-model="excludeSelf"
                :disabled="!xUser"
              />
              <span>본인 계정 제외 (@{{ xUser ? xUser.username : "-" }})</span>
            </label>
            <label :class="$style.checkRow">
              <input type="checkbox" v-model="dedupe" />
              <span>중복 계정 제거</span>
            </label>
            <label :class="$style.checkRow">
              <input type="checkbox" v-model="shareResult" />
              <span>결과를 트윗으로 공유할 수 있도록 미리 준비</span>
            </label>
          </div>
        </div>

        <div :class="$style.actions">
          <button
            type="button"
            :class="[$style.btn, $style.btnGhost]"
            @click="step = 2"
          >
            이전
          </button>
          <button
            type="button"
            :class="[$style.btn, $style.btnDraw]"
            :disabled="!canDraw"
            @click="startDraw"
          >
            🎉 추첨 시작
          </button>
        </div>
      </section>

      <!-- STEP 4: 결과 -->
      <section v-if="step === 4" :class="$style.card">
        <h2 :class="$style.cardTitle">
          {{ drawing ? "추첨 중…" : "🎊 당첨자 발표" }}
        </h2>
        <p v-if="!drawing" :class="$style.cardDesc">
          참여자 {{ eligibleCount.toLocaleString() }}명 중에서
          {{ winners.length }}명이 당첨되었습니다!
        </p>

        <!-- 룰렛 애니메이션 -->
        <div v-if="drawing" :class="$style.roulette">
          <div :class="$style.rouletteWindow">
            <ul
              :class="$style.rouletteList"
              :style="{ transform: `translateY(-${rouletteOffset}px)` }"
            >
              <li
                v-for="(name, i) in rouletteNames"
                :key="i"
                :class="$style.rouletteItem"
              >
                @{{ name }}
              </li>
            </ul>
            <div :class="$style.rouletteCursor"></div>
          </div>
          <p :class="$style.rouletteHint">두근두근… 당첨자를 추첨하고 있어요</p>
        </div>

        <!-- 결과 카드 -->
        <ul v-else :class="$style.winnerList">
          <li
            v-for="(winner, idx) in winners"
            :key="winner.username + idx"
            :class="$style.winnerItem"
            :style="{ animationDelay: `${idx * 80}ms` }"
          >
            <div :class="$style.winnerRank">{{ idx + 1 }}</div>
            <div :class="$style.winnerAvatar">
              <img
                v-if="winner.profile_image_url"
                :src="winner.profile_image_url"
                alt=""
              />
              <span v-else>𝕏</span>
            </div>
            <div :class="$style.winnerMeta">
              <div :class="$style.winnerName">
                {{ winner.name || winner.username }}
              </div>
              <a
                :href="`https://x.com/${winner.username}`"
                target="_blank"
                rel="noopener noreferrer"
                :class="$style.winnerHandle"
              >
                @{{ winner.username }}
              </a>
            </div>
            <button
              type="button"
              :class="[$style.btn, $style.btnGhostSmall]"
              @click="copyHandle(winner.username)"
            >
              복사
            </button>
          </li>
        </ul>

        <div v-if="!drawing" :class="$style.resultActions">
          <button
            type="button"
            :class="[$style.btn, $style.btnGhost]"
            @click="copyAllWinners"
          >
            전체 복사
          </button>
          <button
            v-if="shareResult"
            type="button"
            :class="[$style.btn, $style.btnX]"
            @click="shareToX"
          >
            <span :class="$style.btnXIcon">𝕏</span> 결과 트윗하기
          </button>
          <button
            type="button"
            :class="[$style.btn, $style.btnPrimary]"
            @click="redraw"
          >
            다시 추첨
          </button>
          <button
            type="button"
            :class="[$style.btn, $style.btnGhost]"
            @click="resetAll"
          >
            새 추첨
          </button>
        </div>
      </section>
    </main>
  </div>
</template>

<script>
const TWEET_URL_REGEX =
  /(?:twitter\.com|x\.com)\/[A-Za-z0-9_]+\/status(?:es)?\/(\d{5,25})/i;
const TWEET_ID_REGEX = /^\d{5,25}$/;

const STORAGE_KEY_TOKEN = "rt_raffle_x_token";
const STORAGE_KEY_USER = "rt_raffle_x_user";

export default {
  name: "RtRaffle",
  data() {
    return {
      steps: ["트윗 선택", "참여자 수집", "옵션 설정", "추첨 결과"],
      step: 1,

      tweetInput: "",
      parsedTweetId: "",

      sourceMode: "api",
      manualText: "",

      xAccessToken: "",
      xUser: null,
      xConnecting: false,

      fetchingRetweets: false,
      apiError: "",
      apiHint: "",

      participants: [],

      winnerCount: 1,
      excludeSelf: true,
      dedupe: true,
      shareResult: false,

      drawing: false,
      winners: [],
      rouletteNames: [],
      rouletteOffset: 0,
      rouletteRaf: 0,
      messageHandler: null,
    };
  },
  computed: {
    eligibleCount() {
      return this.computeEligible(this.participants).length;
    },
    canDraw() {
      return (
        this.eligibleCount > 0 &&
        Number.isInteger(this.winnerCount) &&
        this.winnerCount >= 1 &&
        this.winnerCount <= this.eligibleCount
      );
    },
    manualParsed() {
      return this.parseManual(this.manualText);
    },
    participantsPreview() {
      return this.participants.slice(0, 30);
    },
  },
  watch: {
    eligibleCount(val) {
      if (this.winnerCount > val) {
        this.winnerCount = Math.max(1, val);
      }
    },
  },
  created() {
    try {
      const token = sessionStorage.getItem(STORAGE_KEY_TOKEN);
      const user = sessionStorage.getItem(STORAGE_KEY_USER);
      if (token) this.xAccessToken = token;
      if (user) this.xUser = JSON.parse(user);

      // 모바일 콜백에서 돌아온 직후 에러 메시지 표시
      const callbackError = sessionStorage.getItem("rt_raffle_x_error");
      if (callbackError) {
        this.apiError = callbackError;
        sessionStorage.removeItem("rt_raffle_x_error");
      }
    } catch (e) {
      // 무시 - 세션 복구 실패는 치명적이지 않음
    }

    this.messageHandler = (event) => {
      if (event.origin !== window.location.origin) return;
      const data = event.data || {};
      if (data.type === "RT_RAFFLE_X_TOKEN") {
        this.xAccessToken = data.accessToken || "";
        this.xUser = data.user || null;
        if (this.xAccessToken) {
          try {
            sessionStorage.setItem(STORAGE_KEY_TOKEN, this.xAccessToken);
            sessionStorage.setItem(
              STORAGE_KEY_USER,
              JSON.stringify(this.xUser || {})
            );
          } catch (e) {
            // 저장 실패는 무시
          }
        }
        this.xConnecting = false;
      } else if (data.type === "RT_RAFFLE_X_ERROR") {
        this.xConnecting = false;
        this.apiError = data.message || "X 연동 실패";
      }
    };
    window.addEventListener("message", this.messageHandler);
  },
  beforeUnmount() {
    if (this.messageHandler) {
      window.removeEventListener("message", this.messageHandler);
    }
    if (this.rouletteRaf) cancelAnimationFrame(this.rouletteRaf);
  },
  methods: {
    onTweetInput() {
      this.parsedTweetId = this.extractTweetId(this.tweetInput);
    },

    extractTweetId(input) {
      if (!input) return "";
      const trimmed = String(input).trim();
      if (TWEET_ID_REGEX.test(trimmed)) return trimmed;
      const m = trimmed.match(TWEET_URL_REGEX);
      return m ? m[1] : "";
    },

    parseManual(text) {
      if (!text) return [];
      const items = String(text)
        .split(/[\s,;\n\r\t]+/)
        .map((s) => s.replace(/^@+/, "").trim())
        .filter(Boolean);

      const valid = items.filter((s) => /^[A-Za-z0-9_]{1,15}$/.test(s));
      const seen = new Set();
      const result = [];
      for (const username of valid) {
        const key = username.toLowerCase();
        if (seen.has(key)) continue;
        seen.add(key);
        result.push({ username, id: key, name: username });
      }
      return result;
    },

    applyManualList() {
      if (!this.manualParsed.length) return;
      this.participants = this.manualParsed.slice();
      this.apiError = "";
    },

    clearParticipants() {
      this.participants = [];
      this.manualText = "";
      this.apiError = "";
      this.apiHint = "";
    },

    computeEligible(list) {
      let filtered = list.slice();
      if (this.excludeSelf && this.xUser && this.xUser.username) {
        const self = String(this.xUser.username).toLowerCase();
        filtered = filtered.filter(
          (p) => String(p.username || "").toLowerCase() !== self
        );
      }
      if (this.dedupe) {
        const seen = new Set();
        filtered = filtered.filter((p) => {
          const key = String(p.id || p.username || "").toLowerCase();
          if (!key) return false;
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        });
      }
      return filtered;
    },

    /** PKCE & 랜덤 헬퍼 */
    generateRandomString(length) {
      const charset =
        "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~";
      const values = window.crypto.getRandomValues(new Uint8Array(length));
      let result = "";
      values.forEach((v) => (result += charset[v % charset.length]));
      return result;
    },
    async generateCodeChallenge(verifier) {
      const encoder = new TextEncoder();
      const data = encoder.encode(verifier);
      const hashed = await window.crypto.subtle.digest("SHA-256", data);
      const bytes = new Uint8Array(hashed);
      let str = "";
      bytes.forEach((b) => (str += String.fromCharCode(b)));
      return btoa(str).replace(/\+/g, "-").replace(/\//g, "_").replace(/=/g, "");
    },
    getCookie(name) {
      const value = `; ${document.cookie}`;
      const parts = value.split(`; ${name}=`);
      if (parts.length === 2) {
        return decodeURIComponent(parts.pop().split(";").shift());
      }
      return null;
    },

    async connectX() {
      const clientId = process.env.VUE_APP_X_CLIENT_ID;
      if (!clientId) {
        this.apiError =
          "X Client ID가 설정되지 않았습니다. (.env의 VUE_APP_X_CLIENT_ID 확인)";
        return;
      }

      this.xConnecting = true;
      this.apiError = "";

      try {
        document.cookie =
          "x_state=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
        document.cookie =
          "x_code_verifier=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
        document.cookie =
          "x_auth_type=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";

        const codeVerifier = this.generateRandomString(64);
        const codeChallenge = await this.generateCodeChallenge(codeVerifier);
        const state = this.generateRandomString(32);
        const expireTime = new Date(Date.now() + 30 * 60 * 1000);

        document.cookie = `x_code_verifier=${encodeURIComponent(
          codeVerifier
        )}; expires=${expireTime.toUTCString()}; path=/; SameSite=Lax`;
        document.cookie = `x_state=${encodeURIComponent(
          state
        )}; expires=${expireTime.toUTCString()}; path=/; SameSite=Lax`;
        document.cookie = `x_auth_type=raffle; expires=${expireTime.toUTCString()}; path=/; SameSite=Lax`;

        const params = new URLSearchParams({
          response_type: "code",
          client_id: clientId,
          redirect_uri: `${window.location.origin}/x-callback`,
          scope: "tweet.read users.read offline.access",
          state,
          code_challenge: codeChallenge,
          code_challenge_method: "S256",
        });
        const authUrl = `https://x.com/i/oauth2/authorize?${params.toString()}`;

        const isMobile =
          /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
            navigator.userAgent
          );

        if (isMobile) {
          sessionStorage.setItem("rt_raffle_return_path", this.$route.fullPath);
          window.location.href = authUrl;
          return;
        }

        const popup = window.open(
          authUrl,
          "x-oauth",
          "width=600,height=720,scrollbars=yes,resizable=yes"
        );
        if (!popup) {
          this.xConnecting = false;
          this.apiError = "팝업이 차단되었어요. 브라우저 팝업 허용 후 다시 시도해주세요.";
          return;
        }

        const checkClosed = setInterval(() => {
          if (popup.closed) {
            clearInterval(checkClosed);
            setTimeout(() => {
              if (this.xConnecting) {
                this.xConnecting = false;
                this.apiError = "X 연동이 취소되었습니다.";
              }
            }, 800);
          }
        }, 700);
      } catch (e) {
        this.xConnecting = false;
        this.apiError = `X 연동 준비 중 오류: ${e.message || e}`;
      }
    },

    disconnectX() {
      this.xAccessToken = "";
      this.xUser = null;
      try {
        sessionStorage.removeItem(STORAGE_KEY_TOKEN);
        sessionStorage.removeItem(STORAGE_KEY_USER);
      } catch (e) {
        // 무시
      }
    },

    async fetchRetweetUsers() {
      if (!this.parsedTweetId) {
        this.apiError = "트윗 ID를 먼저 확인해주세요.";
        return;
      }
      if (!this.xAccessToken) {
        this.apiError = "X 계정 연동이 필요해요.";
        return;
      }

      this.fetchingRetweets = true;
      this.apiError = "";
      this.apiHint = "";

      try {
        const url = new URL(
          `/api/x/2/tweets/${encodeURIComponent(this.parsedTweetId)}/retweeted_by`,
          window.location.origin
        );
        url.searchParams.set(
          "user.fields",
          "id,name,username,profile_image_url"
        );
        url.searchParams.set("max_results", "100");

        const collected = [];
        let nextToken = "";
        let pages = 0;
        const MAX_PAGES = 10;

        do {
          if (nextToken) url.searchParams.set("pagination_token", nextToken);
          else url.searchParams.delete("pagination_token");

          const res = await fetch(url.toString(), {
            headers: {
              Authorization: `Bearer ${this.xAccessToken}`,
              "Content-Type": "application/json",
            },
          });

          if (!res.ok) {
            const raw = await res.text();
            let parsed;
            try {
              parsed = JSON.parse(raw);
            } catch (e) {
              parsed = { error: raw };
            }
            if (res.status === 401) {
              this.disconnectX();
              throw new Error(
                "인증이 만료되었습니다. X 계정을 다시 연동해주세요."
              );
            }
            if (res.status === 429) {
              throw new Error(
                "X API 호출 한도를 초과했습니다. 잠시 후 다시 시도하거나, 명단 직접 입력 모드를 사용해주세요."
              );
            }
            throw new Error(
              `리트윗 유저 조회 실패 (${res.status}): ${
                parsed.detail || parsed.title || parsed.error || "알 수 없는 오류"
              }`
            );
          }

          const json = await res.json();
          if (Array.isArray(json.data)) collected.push(...json.data);
          nextToken = (json.meta && json.meta.next_token) || "";
          pages += 1;
          if (pages >= MAX_PAGES) {
            this.apiHint = `안전상 ${MAX_PAGES * 100}명까지만 불러왔어요.`;
            break;
          }
        } while (nextToken);

        if (!collected.length) {
          this.apiHint =
            "이 트윗에는 리트윗한 유저가 없거나, 비공개/제한된 트윗일 수 있어요.";
        }

        this.participants = collected.map((u) => ({
          id: u.id,
          username: u.username,
          name: u.name,
          profile_image_url: u.profile_image_url,
        }));
      } catch (e) {
        this.apiError = e.message || String(e);
      } finally {
        this.fetchingRetweets = false;
      }
    },

    /** 추첨 */
    startDraw() {
      if (!this.canDraw) return;
      const pool = this.computeEligible(this.participants);
      const count = Math.min(this.winnerCount, pool.length);
      const finalWinners = this.pickRandom(pool, count);

      this.step = 4;
      this.drawing = true;
      this.winners = [];

      const usernames = pool.map((p) => p.username);
      const loop = [];
      for (let i = 0; i < 50; i++) {
        loop.push(
          usernames[Math.floor(Math.random() * usernames.length)] || "user"
        );
      }
      loop.push(...finalWinners.map((w) => w.username));
      this.rouletteNames = loop;
      this.rouletteOffset = 0;

      const ITEM_HEIGHT = 56;
      const targetOffset = (loop.length - 1) * ITEM_HEIGHT;
      const duration = 2800;
      const start = performance.now();

      const easeOutQuart = (t) => 1 - Math.pow(1 - t, 4);

      const tick = (now) => {
        const t = Math.min(1, (now - start) / duration);
        this.rouletteOffset = targetOffset * easeOutQuart(t);
        if (t < 1) {
          this.rouletteRaf = requestAnimationFrame(tick);
        } else {
          this.drawing = false;
          this.winners = finalWinners;
        }
      };
      this.rouletteRaf = requestAnimationFrame(tick);
    },

    pickRandom(arr, count) {
      const result = arr.slice();
      for (let i = result.length - 1; i > 0; i--) {
        const j = this.secureRandomInt(i + 1);
        [result[i], result[j]] = [result[j], result[i]];
      }
      return result.slice(0, count);
    },

    secureRandomInt(max) {
      if (max <= 0) return 0;
      const buf = new Uint32Array(1);
      window.crypto.getRandomValues(buf);
      return buf[0] % max;
    },

    redraw() {
      this.startDraw();
    },

    resetAll() {
      this.step = 1;
      this.tweetInput = "";
      this.parsedTweetId = "";
      this.manualText = "";
      this.participants = [];
      this.winnerCount = 1;
      this.winners = [];
      this.apiError = "";
      this.apiHint = "";
      this.drawing = false;
    },

    async copyHandle(username) {
      try {
        await navigator.clipboard.writeText(`@${username}`);
      } catch (e) {
        // 무시 - 복사 실패
      }
    },
    async copyAllWinners() {
      const text = this.winners.map((w) => `@${w.username}`).join("\n");
      try {
        await navigator.clipboard.writeText(text);
      } catch (e) {
        // 무시
      }
    },
    shareToX() {
      const lines = this.winners.map((w) => `@${w.username}`).join(" ");
      const intent = new URL("https://twitter.com/intent/tweet");
      const text = `🎉 RT 추첨 당첨자 발표!\n\n${lines}\n\n축하드립니다 🎊`;
      intent.searchParams.set("text", text);
      window.open(intent.toString(), "_blank", "noopener,noreferrer");
    },
  },
};
</script>

<style module>
/* ================================
   Linkook Live 디자인 시스템 매칭
   - 배경: 화이트
   - 폰트: NanumSquareRound (R/B/EB)
   - 액센트: #66bdff → #ff6ba9 그라데이션
   - 텍스트: #111 / #333 / #666
   - 보더: #efefef / #eaeaea
   ================================ */
.container {
  width: 90%;
  max-width: 720px;
  margin: 0 auto;
  padding: 0 0 60px;
  min-height: 100vh;
  background: #fff;
  font-family: "NanumSquareRoundR", sans-serif;
  color: #333;
}

/* 페이지 헤더 (MyProfile 패턴) */
.pageHeader {
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 52px;
  padding: 0 6px 0 4px;
  border-bottom: 1px solid #efefef;
}
.pageHeaderLeft {
  display: flex;
  align-items: center;
  gap: 4px;
}
.headerBackButton {
  background: transparent;
  border: none;
  cursor: pointer;
  width: 36px;
  height: 36px;
  font-size: 20px;
  color: #333;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: background 0.15s ease;
  flex-shrink: 0;
}
.headerBackButton:hover {
  background: #f5f5f5;
}
.pageTitle {
  margin: 0;
  font-size: 19px;
  font-family: "NanumSquareRoundEB", sans-serif;
  font-weight: 700;
  color: #111;
  letter-spacing: -0.3px;
}

/* 히어로 */
.hero {
  text-align: center;
  padding: 28px 20px 22px;
  background: #fff;
}
.heroBadge {
  display: inline-block;
  padding: 6px 14px;
  background: linear-gradient(135deg, #eaf5ff 0%, #ffe9f2 100%);
  color: #1f6fae;
  border-radius: 999px;
  font-size: 12px;
  font-family: "NanumSquareRoundB", sans-serif;
  font-weight: 700;
  margin-bottom: 14px;
  letter-spacing: 0.4px;
}
.heroTitle {
  font-family: "NanumSquareRoundEB", sans-serif;
  font-size: 28px;
  margin: 0 0 8px;
  letter-spacing: -0.5px;
  color: #111;
}
.heroSub {
  color: #666;
  font-size: 14px;
  line-height: 1.6;
  margin: 0;
}

/* 스텝 인디케이터 */
.stepper {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 0 20px;
  margin-bottom: 18px;
  flex-wrap: wrap;
}
.stepperItem {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px 8px 8px;
  border-radius: 999px;
  background: #f6f7f9;
  border: 1px solid #eaeaea;
  color: #888;
  font-size: 13px;
  font-family: "NanumSquareRoundB", sans-serif;
  font-weight: 700;
  transition: all 0.2s ease;
}
.stepperItemActive {
  background: linear-gradient(135deg, #eaf5ff 0%, #ffe9f2 100%);
  border-color: transparent;
  color: #111;
  box-shadow: 0 2px 10px rgba(102, 189, 255, 0.18);
}
.stepperItemDone {
  background: #f1f8ff;
  border-color: #d6ecff;
  color: #1f6fae;
}
.stepperDot {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: #fff;
  border: 1px solid #eaeaea;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  color: #888;
}
.stepperItemActive .stepperDot {
  background: linear-gradient(135deg, #66bdff, #ff6ba9);
  border-color: transparent;
  color: #fff;
}
.stepperItemDone .stepperDot {
  background: #66bdff;
  border-color: #66bdff;
  color: #fff;
}

/* 본문 */
.main {
  padding: 0 20px;
}

/* 카드 */
.card {
  background: #fff;
  border: 1px solid #efefef;
  border-radius: 14px;
  padding: 26px 24px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.03);
}
.cardTitle {
  font-family: "NanumSquareRoundEB", sans-serif;
  font-size: 18px;
  margin: 0 0 6px;
  color: #111;
  letter-spacing: -0.3px;
}
.cardDesc {
  color: #666;
  font-size: 13.5px;
  margin: 0 0 20px;
  line-height: 1.6;
}

/* 폼 필드 */
.field {
  margin-bottom: 16px;
}
.label {
  display: block;
  font-size: 13px;
  color: #4f4f4f;
  margin-bottom: 8px;
  font-family: "NanumSquareRoundB", sans-serif;
  font-weight: 700;
}
.input,
.textarea {
  width: 100%;
  background: #fff;
  border: 1px solid #e5e7eb;
  color: #111;
  font-size: 15px;
  padding: 12px 14px;
  border-radius: 10px;
  outline: none;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
  font-family: "NanumSquareRoundR", sans-serif;
  box-sizing: border-box;
}
.textarea {
  resize: vertical;
  min-height: 140px;
  line-height: 1.6;
}
.input:focus,
.textarea:focus {
  border-color: #66bdff;
  box-shadow: 0 0 0 3px rgba(102, 189, 255, 0.15);
}
.input::placeholder,
.textarea::placeholder {
  color: #b8b8b8;
}

/* 힌트 */
.hintOk {
  margin-top: 8px;
  font-size: 13px;
  color: #1f8a44;
  display: flex;
  align-items: center;
  gap: 6px;
}
.hintErr {
  margin-top: 8px;
  font-size: 13px;
  color: #c62828;
}
.hintMuted {
  margin-top: 8px;
  font-size: 12px;
  color: #888;
}
.hintIcon {
  display: inline-flex;
  width: 18px;
  height: 18px;
  background: #e2f7e9;
  color: #1f8a44;
  border-radius: 50%;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 700;
}
.code {
  background: #f4f5f7;
  padding: 2px 6px;
  border-radius: 5px;
  font-size: 12px;
  color: #111;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
}

/* 버튼 행 */
.actions {
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  margin-top: 22px;
  flex-wrap: wrap;
}

/* 버튼 */
.btn {
  border: 1px solid transparent;
  border-radius: 10px;
  padding: 12px 22px;
  font-size: 14.5px;
  font-family: "NanumSquareRoundB", sans-serif;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.2s ease, box-shadow 0.2s ease, transform 0.06s ease,
    opacity 0.2s ease, color 0.2s ease, border-color 0.2s ease;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  justify-content: center;
  line-height: 1.2;
}
.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.btn:not(:disabled):active {
  transform: translateY(1px);
}

/* Primary: 사이트 시그니처 그라데이션 (blue → pink) */
.btnPrimary {
  background: linear-gradient(135deg, #66bdff, #ff6ba9);
  color: #fff;
  border-color: transparent;
  box-shadow: 0 4px 14px rgba(255, 107, 169, 0.22);
}
.btnPrimary:not(:disabled):hover {
  background: linear-gradient(135deg, #4a9eff, #ff4d94);
  box-shadow: 0 6px 18px rgba(255, 107, 169, 0.3);
  transform: translateY(-1px);
}

/* Ghost (보조) */
.btnGhost {
  background: #fff;
  color: #333;
  border: 1px solid #e5e7eb;
}
.btnGhost:not(:disabled):hover {
  background: #f8f9fb;
  border-color: #d8dbe0;
}

.btnGhostSmall {
  background: #fff;
  color: #4f4f4f;
  border: 1px solid #e5e7eb;
  padding: 7px 12px;
  font-size: 12.5px;
  border-radius: 8px;
  font-family: "NanumSquareRoundB", sans-serif;
  font-weight: 700;
}
.btnGhostSmall:not(:disabled):hover {
  background: #f5f6f8;
  border-color: #d8dbe0;
  color: #111;
}

/* X(검정) 버튼 - MyProfile xButton 패턴 */
.btnX {
  background: #000;
  color: #fff;
  border-color: #000;
}
.btnX:not(:disabled):hover {
  background: #1f1f1f;
  border-color: #1f1f1f;
}
.btnXIcon {
  font-weight: 900;
  font-size: 16px;
  letter-spacing: -0.02em;
}

/* 추첨 시작 버튼 (특별 강조) */
.btnDraw {
  background: linear-gradient(135deg, #66bdff, #ff6ba9);
  color: #fff;
  font-size: 15.5px;
  padding: 14px 26px;
  box-shadow: 0 6px 18px rgba(255, 107, 169, 0.28);
  border-color: transparent;
}
.btnDraw:not(:disabled):hover {
  background: linear-gradient(135deg, #4a9eff, #ff4d94);
  box-shadow: 0 8px 22px rgba(255, 107, 169, 0.35);
  transform: translateY(-1px);
}

.fullWidth {
  width: 100%;
}

/* 세그먼트 탭 */
.tabs {
  display: inline-flex;
  background: #f4f5f7;
  border: 1px solid #ececec;
  padding: 4px;
  border-radius: 10px;
  margin-bottom: 18px;
}
.tab {
  background: transparent;
  border: none;
  color: #666;
  font-size: 13px;
  font-family: "NanumSquareRoundB", sans-serif;
  font-weight: 700;
  padding: 8px 14px;
  border-radius: 7px;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;
}
.tabActive {
  background: #fff;
  color: #111;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
}

.sourcePanel {
  margin-bottom: 12px;
}

/* X 연동 안내 박스 */
.connectBox {
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 22px;
  background: linear-gradient(
    135deg,
    rgba(102, 189, 255, 0.06),
    rgba(255, 107, 169, 0.06)
  );
  border: 1px dashed #d8e7f5;
  border-radius: 12px;
}
.connectInfo {
  text-align: left;
}
.connectTitle {
  font-family: "NanumSquareRoundEB", sans-serif;
  font-size: 16px;
  color: #111;
  margin: 0 0 6px;
}
.connectDesc {
  font-size: 13px;
  color: #555;
  margin: 0;
  line-height: 1.6;
}

/* 연동 완료 박스 */
.connectedBox {
  background: #fff;
  border: 1px solid #efefef;
  border-radius: 12px;
  padding: 16px;
}
.connectedRow {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 14px;
}
.connectedAvatar {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: #111;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 900;
  overflow: hidden;
  flex-shrink: 0;
}
.connectedAvatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.connectedMeta {
  flex: 1;
  min-width: 0;
}
.connectedName {
  font-size: 14.5px;
  color: #111;
  font-family: "NanumSquareRoundB", sans-serif;
  font-weight: 700;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.connectedHandle {
  font-size: 13px;
  color: #666;
}

/* 스피너 */
.spinnerSm {
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255, 255, 255, 0.35);
  border-top-color: #fff;
  border-radius: 50%;
  animation: rt-spin 0.8s linear infinite;
}
@keyframes rt-spin {
  to {
    transform: rotate(360deg);
  }
}

/* 메시지 박스 */
.errorMsg {
  margin-top: 12px;
  background: #fff3f4;
  border: 1px solid #ffd5d9;
  color: #c62828;
  padding: 10px 14px;
  border-radius: 10px;
  font-size: 13px;
  line-height: 1.6;
}
.helperMsg {
  margin-top: 10px;
  color: #666;
  font-size: 13px;
  line-height: 1.6;
}

/* 참여자 미리보기 */
.participantsBox {
  margin-top: 18px;
  padding: 16px;
  background: #fafbfc;
  border: 1px solid #eef0f3;
  border-radius: 12px;
}
.participantsHeader {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}
.participantsTitle {
  margin: 0;
  font-size: 14.5px;
  color: #111;
  font-family: "NanumSquareRoundEB", sans-serif;
}
.chipList {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px 4px 4px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 999px;
  font-size: 12.5px;
  color: #333;
}
.chipAvatar,
.chipAvatarImg {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: linear-gradient(135deg, #66bdff, #ff6ba9);
  color: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-family: "NanumSquareRoundEB", sans-serif;
  font-weight: 700;
  overflow: hidden;
}
.chipAvatarImg img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.chipText {
  font-family: "NanumSquareRoundB", sans-serif;
  font-weight: 700;
}
.chipMore {
  display: inline-flex;
  align-items: center;
  padding: 4px 12px;
  background: #f4f5f7;
  border-radius: 999px;
  font-size: 12px;
  color: #666;
}

/* 옵션 그리드 */
.optionGrid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
}
@media (max-width: 640px) {
  .optionGrid {
    grid-template-columns: 1fr;
    gap: 16px;
  }
}
.checkboxGroup {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 24px;
}
.checkRow {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 13.5px;
  color: #333;
  cursor: pointer;
}
.checkRow input[type="checkbox"] {
  width: 17px;
  height: 17px;
  accent-color: #66bdff;
  cursor: pointer;
}

/* 룰렛 */
.roulette {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 18px 0 8px;
}
.rouletteWindow {
  position: relative;
  width: 100%;
  max-width: 380px;
  height: 56px;
  background: linear-gradient(135deg, #66bdff, #ff6ba9);
  border-radius: 12px;
  overflow: hidden;
  margin-bottom: 16px;
  padding: 2px;
}
.rouletteWindow::after {
  content: "";
  position: absolute;
  inset: 2px;
  border-radius: 10px;
  background: #fff;
  z-index: 0;
}
.rouletteList {
  list-style: none;
  padding: 0;
  margin: 0;
  position: relative;
  z-index: 1;
  will-change: transform;
}
.rouletteItem {
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: "NanumSquareRoundEB", sans-serif;
  font-size: 19px;
  color: #111;
}
.rouletteCursor {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  border-top: 2px solid rgba(255, 255, 255, 0.65);
  border-bottom: 2px solid rgba(255, 255, 255, 0.65);
  pointer-events: none;
  z-index: 2;
}
.rouletteHint {
  color: #666;
  font-size: 13px;
  margin: 0;
}

/* 당첨자 카드 */
.winnerList {
  list-style: none;
  padding: 0;
  margin: 4px 0 22px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.winnerItem {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px;
  background: #fff;
  border: 1px solid #f0e4ec;
  border-radius: 12px;
  box-shadow: 0 4px 14px rgba(255, 107, 169, 0.08);
  position: relative;
  overflow: hidden;
  opacity: 0;
  transform: translateY(8px);
  animation: rt-pop 0.5s ease forwards;
}
.winnerItem::before {
  content: "";
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 4px;
  background: linear-gradient(180deg, #66bdff, #ff6ba9);
}
@keyframes rt-pop {
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
.winnerRank {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: linear-gradient(135deg, #66bdff, #ff6ba9);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: "NanumSquareRoundEB", sans-serif;
  font-size: 13px;
  flex-shrink: 0;
  margin-left: 6px;
}
.winnerAvatar {
  width: 42px;
  height: 42px;
  border-radius: 50%;
  background: #111;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 900;
  overflow: hidden;
  flex-shrink: 0;
}
.winnerAvatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.winnerMeta {
  flex: 1;
  min-width: 0;
}
.winnerName {
  font-family: "NanumSquareRoundEB", sans-serif;
  font-size: 14.5px;
  color: #111;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.winnerHandle {
  font-size: 13px;
  color: #1f6fae;
  text-decoration: none;
  font-family: "NanumSquareRoundR", sans-serif;
}
.winnerHandle:hover {
  text-decoration: underline;
}

.resultActions {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  justify-content: flex-end;
}

/* 반응형 */
@media (max-width: 640px) {
  .container {
    width: 100%;
    padding: 0 0 80px;
  }
  .hero {
    padding: 22px 18px 16px;
  }
  .heroTitle {
    font-size: 23px;
  }
  .heroSub {
    font-size: 13px;
  }
  .main {
    padding: 0 16px;
  }
  .card {
    padding: 20px 18px;
    border-radius: 12px;
  }
  .cardTitle {
    font-size: 16.5px;
  }
  .stepperLabel {
    display: none;
  }
  .stepperItem {
    padding: 6px 10px 6px 6px;
  }
  .stepperDot {
    width: 20px;
    height: 20px;
    font-size: 11px;
  }
  .btn {
    padding: 11px 16px;
    font-size: 14px;
  }
  .btnDraw {
    padding: 12px 18px;
    font-size: 14.5px;
  }
  .actions {
    justify-content: stretch;
  }
  .actions .btn {
    flex: 1;
  }
}
</style>
